'use client';

import * as React from 'react';

import {
  ChartLegend as RechartsChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';
import { cn, warnOnce } from '@/hen-ui/utils';
import { Tooltip } from '../Tooltip';
import { expandLegendEntries, type LegendEntry } from '../chart-color-rules';
import type { ColorScale, SeriesColorConfig } from '../chart-types';

/**
 * Props for the hen-ui {@link ChartLegend}. `content` and `payload` are
 * owned internally so the series-order contract cannot be bypassed.
 *
 * @see {@link RechartsChartLegend} — underlying shadcn / Recharts primitive.
 */
export type ChartLegendProps = Omit<
  React.ComponentProps<typeof RechartsChartLegend>,
  'content' | 'payload'
> & {
  /**
   * Series keys in declared order. Drives DOM order and swatch colors via
   * `var(--color-${key})`. Without this override, Recharts 3.x defaults
   * `itemSorter='value'`, which sorts the legend alphabetically.
   */
  series: readonly string[];
  /**
   * Chart config used to resolve human-readable labels for each series key.
   * Required when `clickableLegend` is `true` so the interactive render can
   * display `config[key].label` instead of the raw key string.
   */
  config?: ChartConfig;
  /**
   * When `true`, each legend item renders as an interactive toggle button.
   * Clicking (or pressing Enter / Space) hides / shows the corresponding
   * series in the chart. Cannot hide the last visible series.
   *
   * Requires `hiddenSeries` + `onSeriesToggle` to be wired from the parent.
   */
  clickableLegend?: boolean;
  /**
   * Set of series keys currently hidden. Controlled by the parent chart.
   * Only used when `clickableLegend` is `true`.
   */
  hiddenSeries?: ReadonlySet<string>;
  /**
   * Called when the user toggles a legend item. Receives the series key.
   * Only used when `clickableLegend` is `true`.
   */
  onSeriesToggle?: (key: string) => void;
  /**
   * Per-series color config. When supplied, the legend expands each
   * series into 1..N entries (one per distinct semantic color it can
   * render on-chart) via {@link expandLegendEntries} — so a series with
   * `{semanticColor: 'positive', colorRules: [{color: 'negative'}]}`
   * renders as two entries: `Revenue` (green) and `Revenue (Negative)`
   * (red). Multiple entries that share the same underlying series toggle
   * together when `clickableLegend` is `true`.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Active color scale. When `'diverging'`, the legend expands each
   * scale-driven series into its two arm swatches (low `< 0` / high `≥ 0`)
   * so it matches the bi-color bars/dots instead of advertising only the
   * high arm. `'categorical'` / `'sequential'` / unset render unchanged.
   */
  colorScale?: ColorScale | string | null;
  /** Diverging family name, forwarded to the arm-color resolver. */
  divergingPalette?: string | null;
  /**
   * For sign-pure series (each key only renders one arm, e.g. WaterfallChart),
   * maps the series key to its single arm so the legend shows one swatch
   * rather than both arms.
   */
  divergingArmByKey?: Record<string, 'low' | 'high'>;
};

// ── LegendSwatch ──────────────────────────────────────────────────────────────

const LegendSwatch: React.FC<{
  color: string;
  className?: string;
}> = ({ color, className }) => (
  <div
    className={cn('h-2 w-2 shrink-0 rounded-xxs', className)}
    style={{ backgroundColor: color }}
    aria-hidden='true'
  />
);

// ── Static (non-interactive) legend ───────────────────────────────────────────

interface StaticLegendContentProps {
  entries: readonly LegendEntry[];
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

/**
 * Non-interactive legend rendered from the expanded entry list — one
 * row per `(series × color)` pair.
 */
const StaticLegendContent: React.FC<StaticLegendContentProps> = ({
  entries,
  verticalAlign,
}) => (
  <div
    className={cn(
      'flex flex-wrap items-center justify-center gap-x-4 gap-y-2',
      verticalAlign === 'top' ? 'pb-3' : 'pt-3',
    )}
  >
    {entries.map((entry) => (
      <div
        key={entry.id}
        className='flex min-w-0 items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-neutral-500'
      >
        <LegendSwatch color={entry.color} />
        <span>{entry.label}</span>
      </div>
    ))}
  </div>
);

// ── Interactive legend ────────────────────────────────────────────────────────

interface InteractiveLegendContentProps {
  entries: readonly LegendEntry[];
  hiddenSeries: ReadonlySet<string>;
  onSeriesToggle: (key: string) => void;
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

const InteractiveLegendContent: React.FC<InteractiveLegendContentProps> = ({
  entries,
  hiddenSeries,
  onSeriesToggle,
  verticalAlign,
}) => {
  // Count unique series (not entries) that are still visible — toggling
  // any entry hides/shows the entire series, so "last visible" is a
  // per-series notion. Memoized together to avoid re-allocating the Set
  // + filtering the array on every render.
  const visibleSeriesCount = React.useMemo(() => {
    const seen = new Set<string>();
    let count = 0;
    for (const e of entries) {
      if (seen.has(e.seriesKey)) continue;
      seen.add(e.seriesKey);
      if (!hiddenSeries.has(e.seriesKey)) count++;
    }
    return count;
  }, [entries, hiddenSeries]);

  // When a series is expanded into multiple entries (base + per-rule
  // color keys, or the `auto-by-value` Positive/Negative pair), only the
  // FIRST entry per series acts as the togglable button. Subsequent
  // entries render as informational color keys — they describe a color
  // the chart can render under conditions, not a series toggle. Without
  // this, clicking "Margin (Negative)" would hide the entire `margin`
  // series, which is a confusing UX (reported in code review).
  const seriesToggleAnchor = React.useMemo(() => {
    const firstEntryIdPerSeries = new Map<string, string>();
    for (const e of entries) {
      if (!firstEntryIdPerSeries.has(e.seriesKey)) {
        firstEntryIdPerSeries.set(e.seriesKey, e.id);
      }
    }
    return firstEntryIdPerSeries;
  }, [entries]);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-2',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
      )}
    >
      {entries.map((entry) => {
        const isToggleAnchor =
          seriesToggleAnchor.get(entry.seriesKey) === entry.id;
        const isHidden = hiddenSeries.has(entry.seriesKey);

        // Informational color-key chip (non-anchor entries). Renders the
        // same way the static legend does — swatch + label, no button
        // affordances, no toggle behaviour, no strikethrough when the
        // series is hidden (the anchor already communicates that).
        if (!isToggleAnchor) {
          return (
            <div
              key={entry.id}
              className={cn(
                'flex min-w-0 items-center gap-1.5',
                isHidden && 'opacity-50',
              )}
            >
              <LegendSwatch color={entry.color} />
              <span>{entry.label}</span>
            </div>
          );
        }

        const isLastVisible = !isHidden && visibleSeriesCount === 1;

        // Render button with conditional behavior.
        // Note: We use aria-disabled + JavaScript guards instead of the native
        // `disabled` attribute because `disabled` blocks pointer events entirely,
        // which prevents the Tooltip wrapper from receiving clicks. This approach
        // maintains proper accessibility semantics while allowing the tooltip to
        // appear on click.
        const buttonEl = (
          <button
            type='button'
            role='checkbox'
            aria-checked={!isHidden}
            aria-disabled={isLastVisible || undefined}
            className={cn(
              'flex min-w-0 items-center gap-1.5 rounded px-1 -mx-1 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 transition-colors',
              isHidden ? 'opacity-50' : 'opacity-100',
              isLastVisible
                ? 'cursor-default'
                : 'cursor-pointer hover:bg-neutral-100',
            )}
            onClick={() => {
              if (!isLastVisible) onSeriesToggle(entry.seriesKey);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (isLastVisible) return;
                e.preventDefault();
                onSeriesToggle(entry.seriesKey);
              }
            }}
          >
            <LegendSwatch color={entry.color} />
            <span className={cn(isHidden && 'line-through')}>
              {entry.label}
            </span>
          </button>
        );

        if (isLastVisible) {
          return (
            <Tooltip
              key={entry.id}
              content='At least one series must remain visible'
              trigger='click'
              side='top'
            >
              {buttonEl}
            </Tooltip>
          );
        }

        return <React.Fragment key={entry.id}>{buttonEl}</React.Fragment>;
      })}
    </div>
  );
};

// ── ChartLegend ───────────────────────────────────────────────────────────────

/**
 * Canonical hen-ui chart legend. Wraps {@link RechartsChartLegend} with
 * an explicit ordered `payload` derived from `series[]`, so legend order
 * follows the declared series rather than Recharts' default alphabetical
 * sort.
 *
 * When `clickableLegend` is `true`, renders an interactive toggle version
 * where each item can be clicked to hide / show the corresponding series.
 *
 * When `seriesColorConfigs` is supplied, each series with `semanticColor`
 * / `colorRules` / `'auto-by-value'` is expanded into multiple legend
 * entries (one per distinct color) so the legend reflects every color
 * the chart can render — keeping legend / tooltip / on-chart marks in
 * lockstep.
 *
 * @example
 * ```tsx
 * <LineChart data={data} config={config} series={['F26', 'F24', 'F25']}>
 *   <ChartLegend series={['F26', 'F24', 'F25']} verticalAlign='bottom' />
 * </LineChart>
 * // legend renders: Fall 2026 · Fall 2024 · Fall 2025
 * ```
 *
 * @see {@link ChartLegendProps}
 */
export function ChartLegend({
  series,
  config,
  clickableLegend = false,
  hiddenSeries,
  onSeriesToggle,
  seriesColorConfigs,
  colorScale,
  divergingPalette,
  divergingArmByKey,
  ...legendProps
}: ChartLegendProps) {
  const isDiverging = colorScale === 'diverging';
  // Dev warning: detect missing wiring at the boundary before applying defaults.
  // Placed in useEffect (like useInteractiveLegend) to keep the render phase
  // pure and avoid duplicate logs in React StrictMode.
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (
        clickableLegend &&
        (hiddenSeries === undefined || onSeriesToggle === undefined)
      ) {
        warnOnce(
          'ChartLegend/interactive-missing-wiring',
          '[hen-ui/ChartLegend] `clickableLegend` is true but `hiddenSeries` and/or `onSeriesToggle` are undefined. Legend buttons will not respond to clicks. Ensure the parent chart passes `hiddenSeries` state and `onSeriesToggle` callback.',
        );
      }
    }
  }, [clickableLegend, hiddenSeries, onSeriesToggle]);

  // Expand series into per-(series × color) entries when seriesColorConfigs
  // is provided. Otherwise, each series maps to a single entry — keeps
  // the default code path unchanged for callers that don't use semantic
  // color rules.
  const entries = React.useMemo<LegendEntry[]>(
    () =>
      expandLegendEntries(series, config, seriesColorConfigs, {
        colorScale,
        divergingPalette,
        armByKey: divergingArmByKey,
      }),
    [
      series,
      config,
      seriesColorConfigs,
      colorScale,
      divergingPalette,
      divergingArmByKey,
    ],
  );

  if (clickableLegend) {
    const resolvedHidden = hiddenSeries ?? new Set<string>();
    const resolvedToggle = onSeriesToggle ?? (() => {});
    return (
      <RechartsChartLegend
        {...legendProps}
        content={() => (
          <InteractiveLegendContent
            entries={entries}
            hiddenSeries={resolvedHidden}
            onSeriesToggle={resolvedToggle}
            verticalAlign={legendProps.verticalAlign}
          />
        )}
      />
    );
  }

  // When `seriesColorConfigs` is supplied — OR a diverging scale expands
  // each series into its two arm swatches — we render our own static item
  // list, since the expanded entries can outnumber `series`, which the
  // shadcn `ChartLegendContent` doesn't model.
  if (seriesColorConfigs || isDiverging) {
    return (
      <RechartsChartLegend
        {...legendProps}
        content={() => (
          <StaticLegendContent
            entries={entries}
            verticalAlign={legendProps.verticalAlign}
          />
        )}
      />
    );
  }

  return (
    <RechartsChartLegend
      {...legendProps}
      content={(props) => (
        // Cast reconciles only the `ref` typing — shadcn ChartLegendContent
        // intersects `<div>`'s ref with Recharts' ReactElement-typed ref.
        <ChartLegendContent
          {...(props as React.ComponentProps<typeof ChartLegendContent>)}
          payload={series.map((key) => ({
            value: key,
            type: 'square',
            id: key,
            // This branch only runs when `seriesColorConfigs` is undefined
            // (and `clickableLegend` is false), so the swatch is always
            // the palette CSS var. We don't call `resolveSeriesBaseColor`
            // here because it would just short-circuit to this same value.
            color: `var(--color-${key})`,
            dataKey: key,
          }))}
        />
      )}
    />
  );
}
ChartLegend.displayName = 'ChartLegend';
